/**
 * Show/hide rule for the post-login integrations gate, kept free of React and
 * CSS imports so it can be unit-tested directly.
 */

export interface HarvestGateStatus {
    connected: boolean
    /** Null when the server has no Harvest OAuth credentials configured. */
    authUrl: string | null
}

export interface CalendarGateStatus {
    connected: boolean
    needsReconnect: boolean
    /** Empty when the server has no Google OAuth credentials configured. */
    connectUrl: string
}

export const INTEGRATIONS_SKIP_KEY = 'hub.integrations-skip'

/**
 * Fired on `window` after a client-side integration connect (e.g. the Harvest
 * OAuth code exchange) so the gate re-reads connection status without a manual
 * page refresh.
 */
export const INTEGRATIONS_CHANGED_EVENT = 'integrations:changed'

/** A connection the user can and should still make. */
export const harvestNeedsConnect = (harvest: HarvestGateStatus): boolean =>
    !harvest.connected && harvest.authUrl !== null

export const calendarNeedsConnect = (calendar: CalendarGateStatus): boolean =>
    (!calendar.connected || calendar.needsReconnect) && calendar.connectUrl !== ''

/**
 * The gate renders only when both statuses have loaded (no flash while
 * unknown), the user hasn't skipped it this session, and at least one
 * integration is still connectable. Unconfigured environments (no OAuth
 * credentials → no connect URL) never gate.
 */
export const shouldShowIntegrationsGate = (
    harvest: HarvestGateStatus | null,
    calendar: CalendarGateStatus | null,
    dismissed: boolean,
): boolean => {
    if (dismissed) return false
    if (harvest === null || calendar === null) return false
    return harvestNeedsConnect(harvest) || calendarNeedsConnect(calendar)
}
